import * as React from 'react';

import { Campo } from '@/componentes/campo';
import { aplicarMascara, editarPorTecla, type Edicao } from '@/componentes/mascara-moeda';
import { Input } from '@/componentes/ui/input';
import { formatarMoedaSemSimbolo, parseNum } from '@/design/formato';
import { cn } from '@/lib/utils';

/* CampoMoeda — o único controle que a biblioteca não entrega.
 *
 * A regra da máscara mora em `mascara-moeda.ts` (texto e cursor, sem DOM): o
 * dígito digitado entra na parte INTEIRA e os centavos já estão na tela desde
 * a primeira tecla. 1-2-5-0 é "1.250,00"; quem quer centavos anda para trás
 * ou tecla a vírgula, que pula para eles. Aqui ficam só os três cuidados de
 * DOM que fazem a diferença entre "funciona" e "dá raiva":
 *
 *  1. o cursor é reposicionado depois da pintura, senão o React o joga para o
 *     fim a cada tecla;
 *  2. tecla que a máscara resolve (vírgula, Backspace, Delete) é interceptada
 *     no keydown, porque depois que o navegador editou o texto já não dá para
 *     saber se ele comeu um dígito ou um separador;
 *  3. o valor vindo de fora (props) só sobrescreve o texto quando o campo NÃO
 *     está com foco, senão um refetch apaga o que a pessoa está digitando.
 *
 * Em teclado virtual de Android o keydown chega sem tecla identificável; ali
 * a edição cai no caminho do `change`, que continua correto, só sem o pulo
 * fino do cursor.
 *
 * ── A LARGURA É A DO DADO, NÃO A DA COLUNA ───────────────────────────────
 * O controle não se estica até a largura que a grade oferecer: um campo de
 * percentual com 600px de comprimento promete quarenta caracteres para
 * receber quatro, e o par rótulo/valor se perde no caminho até a borda. Como
 * a natureza do dado JÁ está declarada pelo sufixo, a medida sai dela: com
 * "%" é dado curto, sem "%" é moeda. É máximo e não largura fixa, então no
 * celular o campo continua encolhendo. */

export type CampoMoedaProps = Omit<
  React.ComponentProps<'input'>,
  'value' | 'defaultValue' | 'onChange' | 'type'
> & {
  valor: number | null;
  aoMudar: (valor: number | null) => void;
  rotulo?: string | undefined;
  ajuda?: string | undefined;
  erro?: string | undefined;
  obrigatorio?: boolean | undefined;
  /** Texto colado à esquerda dentro do campo. Padrão "R$". */
  prefixo?: string | null | undefined;
  /** Texto colado à direita (ex.: "%", "kWp"). */
  sufixo?: string | null | undefined;
  /** Classe do bloco inteiro (rótulo + campo + ajuda). */
  className?: string | undefined;
  /** Classe aplicada só ao <input>. */
  classeControle?: string | undefined;
};

export function CampoMoeda({
  valor,
  aoMudar,
  rotulo,
  ajuda,
  erro,
  obrigatorio,
  prefixo = 'R$',
  sufixo,
  className,
  classeControle,
  ref,
  onBlur,
  onKeyDown,
  // `id` sai de `resto` de propósito. O `<Input>` abaixo espalha `{...resto}`
  // DEPOIS de `{...atributos}`, então um id vindo do chamador sobrescreveria o
  // que o `Campo` gerou e que o `<Label htmlFor>` aponta, deixando o rótulo
  // órfão em silêncio (WCAG 1.3.1). Quem manda no id é o `Campo`, que já
  // aceita um externo e faz a associação junto.
  id,
  ...resto
}: CampoMoedaProps) {
  const interno = React.useRef<HTMLInputElement | null>(null);
  const cursorPendente = React.useRef<number | null>(null);
  const [texto, setTexto] = React.useState(() => formatarMoedaSemSimbolo(valor));

  // Reposiciona o cursor DEPOIS que o React pintou o novo texto.
  React.useLayoutEffect(() => {
    const el = interno.current;
    const pos = cursorPendente.current;
    cursorPendente.current = null;
    if (el && pos !== null) el.setSelectionRange(pos, pos);
  });

  // Valor que chega de fora (carregou do servidor, outra tela mudou) só entra
  // quando ninguém está digitando aqui.
  React.useEffect(() => {
    if (document.activeElement === interno.current) return;
    setTexto((atual) => {
      const alvo = formatarMoedaSemSimbolo(valor);
      if (atual !== '' && parseNum(atual) === (valor ?? 0)) return atual;
      return alvo;
    });
  }, [valor]);

  function aplicar(el: HTMLInputElement, edicao: Edicao) {
    // Estado igual não repinta, e sem repintura ninguém corrige o DOM nem
    // move o cursor: nesse caso quem faz as duas coisas é este bloco.
    if (edicao.texto === texto) {
      if (el.value !== edicao.texto) el.value = edicao.texto;
      el.setSelectionRange(edicao.cursor, edicao.cursor);
      return;
    }
    cursorPendente.current = edicao.cursor;
    setTexto(edicao.texto);
    aoMudar(edicao.texto === '' ? null : parseNum(edicao.texto));
  }

  function aoDigitar(evento: React.ChangeEvent<HTMLInputElement>) {
    const el = evento.currentTarget;
    aplicar(el, aplicarMascara(el.value, el.selectionStart ?? el.value.length));
  }

  function aoTeclar(evento: React.KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(evento);
    // Atalho do sistema (copiar, colar, selecionar tudo) não é edição.
    if (evento.defaultPrevented || evento.ctrlKey || evento.metaKey || evento.altKey) return;

    const el = evento.currentTarget;
    const edicao = editarPorTecla({
      tecla: evento.key,
      texto: el.value,
      inicio: el.selectionStart ?? 0,
      fim: el.selectionEnd ?? 0,
    });
    if (!edicao) return;

    evento.preventDefault();
    aplicar(el, edicao);
  }

  function aoSair(evento: React.FocusEvent<HTMLInputElement>) {
    // A máscara já entrega texto canônico a cada tecla; isto aqui só fecha a
    // porta do que entra sem passar por ela (autopreenchimento do navegador).
    if (texto.trim() === '') {
      aoMudar(null);
    } else {
      const n = parseNum(texto);
      setTexto(formatarMoedaSemSimbolo(n));
      aoMudar(n);
    }
    onBlur?.(evento);
  }

  return (
    <Campo
      id={id}
      rotulo={rotulo}
      ajuda={ajuda}
      erro={erro}
      obrigatorio={obrigatorio}
      className={className}
    >
      {(atributos) => (
        <div className="relative">
          {prefixo ? (
            <span
              className="numero text-tinta-3 pointer-events-none absolute inset-y-0 left-3 flex items-center text-nota"
              aria-hidden="true"
            >
              {prefixo}
            </span>
          ) : null}

          <Input
            {...atributos}
            {...resto}
            ref={(no) => {
              interno.current = no;
              if (typeof ref === 'function') ref(no);
              else if (ref) ref.current = no;
            }}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={texto}
            onChange={aoDigitar}
            onKeyDown={aoTeclar}
            onBlur={aoSair}
            className={cn(
              'numero text-right font-medium',
              prefixo ? 'pl-9' : undefined,
              sufixo ? 'pr-9' : undefined,
              classeControle,
            )}
          />

          {sufixo ? (
            <span
              className="numero text-tinta-3 pointer-events-none absolute inset-y-0 right-3 flex items-center text-nota"
              aria-hidden="true"
            >
              {sufixo}
            </span>
          ) : null}
        </div>
      )}
    </Campo>
  );
}
